import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MdFilterList } from 'react-icons/md';
import saApi from '../../services/superAdminApi';
import { humanizeAction, isDestructiveAction } from './auditActions';

const ACTOR_TYPE_LABELS = {
  super_admin: 'Superadmin',
  club_admin:  'Club Admin',
  system:      'System',
};

const ACTION_GROUPS = [
  { label: 'All actions',      value: '' },
  { label: 'Login',            value: 'SA_LOGIN' },
  { label: 'Club Suspended',   value: 'SA_SUSPEND_CLUB' },
  { label: 'Club Reactivated', value: 'SA_REACTIVATE_CLUB' },
  { label: 'Subscription',     value: 'SA_UPDATE_SUBSCRIPTION' },
  { label: 'Impersonation',    value: 'SA_START_IMPERSONATION' },
  { label: 'Team Role',        value: 'SA_UPDATE_TEAM_ROLE' },
];

const PAGE_SIZE = 20;

export default function AuditLogs() {
  const [action,    setAction]    = useState('');
  const [actorType, setActorType] = useState('');
  const [from,      setFrom]      = useState('');
  const [to,        setTo]        = useState('');
  const [search,    setSearch]    = useState('');
  const [page,      setPage]      = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  const { data, isPending } = useQuery({
    queryKey: ['sa-audit', action, actorType, from, to, page],
    queryFn:  () => saApi.get('/api/super-admin/audit-logs', {
      params: {
        action:    action    || undefined,
        actorType: actorType || undefined,
        from:      from      || undefined,
        to:        to        || undefined,
        page,
        limit: PAGE_SIZE,
      },
    }).then(r => r.data),
  });

  const logs = (data?.logs || []).filter(log => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (log.actorName  || '').toLowerCase().includes(q) ||
      (log.targetName || '').toLowerCase().includes(q) ||
      humanizeAction(log.action).toLowerCase().includes(q) ||
      (log.action     || '').toLowerCase().includes(q)
    );
  });

  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // colorScheme:'dark' keeps native <select>/<input type=date> pickers dark (no white/blue flash).
  const controlStyle = { background: '#1a1918', borderColor: 'rgba(237,232,224,0.08)', colorScheme: 'dark' };

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#ede8e0]">Audit Logs</h1>
          <p className="text-sm mt-1" style={{ color: '#8a847c' }}>Immutable record of all platform actions.</p>
        </div>
        <button
          onClick={() => setShowFilters(v => !v)}
          className="flex items-center gap-2 px-3 h-10 rounded-full text-sm font-medium transition-all duration-200"
          style={{ background: showFilters ? '#28261f' : '#1a1918', color: '#c5bfb5', border: '1px solid rgba(237,232,224,0.10)' }}>
          <MdFilterList /> Filters
        </button>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-3">
        <input value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search actor, target, action…"
          className="border rounded-full px-4 h-10 text-sm outline-none text-[#ede8e0] placeholder-[#6b6560] w-56"
          style={{ background: '#1a1918', borderColor: 'rgba(237,232,224,0.08)' }} />

        <select value={action} onChange={e => { setAction(e.target.value); setPage(1); }}
          className="border rounded-full px-4 h-10 text-sm outline-none text-[#ede8e0] cursor-pointer"
          style={controlStyle}>
          {ACTION_GROUPS.map(g => <option key={g.value} value={g.value}>{g.label}</option>)}
        </select>
      </div>

      {showFilters && (
        <div className="flex flex-wrap gap-3 p-4 rounded-2xl border" style={{ background: '#1e1d1b', borderColor: 'rgba(237,232,224,0.08)' }}>
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: '#6b6560' }}>Actor type</label>
            <select value={actorType} onChange={e => { setActorType(e.target.value); setPage(1); }}
              className="border rounded-full px-3 h-10 text-sm outline-none text-[#ede8e0] cursor-pointer"
              style={controlStyle}>
              <option value="">All types</option>
              {Object.entries(ACTOR_TYPE_LABELS).map(([v, label]) => (
                <option key={v} value={v}>{label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: '#6b6560' }}>From date</label>
            <input type="date" value={from} onChange={e => { setFrom(e.target.value); setPage(1); }}
              className="border rounded-full px-3 h-10 text-sm outline-none text-[#ede8e0]"
              style={controlStyle} />
          </div>
          <div>
            <label className="block text-xs font-medium mb-1.5" style={{ color: '#6b6560' }}>To date</label>
            <input type="date" value={to} onChange={e => { setTo(e.target.value); setPage(1); }}
              className="border rounded-full px-3 h-10 text-sm outline-none text-[#ede8e0]"
              style={controlStyle} />
          </div>
          {(actorType || from || to) && (
            <div className="self-end">
              <button onClick={() => { setActorType(''); setFrom(''); setTo(''); setPage(1); }}
                className="px-3 h-10 rounded-full text-sm transition-all duration-200"
                style={{ background: '#28261f', color: '#c5bfb5' }}>
                Clear
              </button>
            </div>
          )}
        </div>
      )}

      <div className="rounded-2xl border overflow-hidden" style={{ borderColor: 'rgba(237,232,224,0.08)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr style={{ background: '#1a1918', borderBottom: '1px solid rgba(237,232,224,0.08)' }}>
              {['Timestamp', 'Actor', 'Action', 'Target', 'IP'].map(h => (
                <th key={h} className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider" style={{ color: '#6b6560' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isPending ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center" style={{ color: '#6b6560' }}>Loading…</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center" style={{ color: '#6b6560' }}>No logs found</td></tr>
            ) : logs.map(log => {
              const isSystem   = !log.actorName;
              const destructive = isDestructiveAction(log.action);
              return (
                <tr key={log._id} style={{ borderBottom: '1px solid rgba(237,232,224,0.05)', background: '#1e1d1b' }}
                  className="hover:bg-[#28261f] transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap text-xs" style={{ color: '#8a847c' }}>
                    {new Date(log.timestamp).toLocaleString()}
                  </td>
                  <td className="px-4 py-3">
                    <div style={{ color: isSystem ? '#8a847c' : '#ede8e0' }}>
                      {isSystem ? 'TrainAxis System' : log.actorName}
                    </div>
                    {!isSystem && (
                      <div className="text-xs capitalize" style={{ color: '#8a847c' }}>
                        {ACTOR_TYPE_LABELS[log.actorType] || log.actorType}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs font-medium"
                      style={{ color: destructive ? '#f87171' : '#c5bfb5' }}>
                      {humanizeAction(log.action)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {log.targetName ? (
                      <div>
                        <div style={{ color: '#ede8e0' }}>{log.targetName}</div>
                        <div className="text-xs" style={{ color: '#8a847c' }}>{log.targetType}</div>
                      </div>
                    ) : <span style={{ color: '#6b6560' }}>—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono" style={{ color: '#8a847c' }}>
                    {log.ipAddress || '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination — matches the Activity Log style */}
      {total > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-xs" style={{ color: '#8a847c' }}>
            Page {page} of {pages} · {total.toLocaleString()} total entries
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="px-3 py-1.5 text-xs font-semibold border rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#28261f]"
              style={{ borderColor: 'rgba(237,232,224,0.10)', color: '#c5bfb5' }}>
              Previous
            </button>
            <button
              onClick={() => setPage(p => Math.min(pages, p + 1))}
              disabled={page >= pages}
              className="px-3 py-1.5 text-xs font-semibold border rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-[#28261f]"
              style={{ borderColor: 'rgba(237,232,224,0.10)', color: '#c5bfb5' }}>
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
